import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { IoMdArrowDroprightCircle, IoMdArrowDropdownCircle } from 'react-icons/io';
import { Spinner } from 'react-bootstrap';
import { DAY_ORDER, formatWeekDate, formatCurrencyFull, formatCurrencyCompact, calculateDayTotals, buildHierarchy, SCROLL_DELAY_MS } from '../utils/costingHelpers';

const HierarchicalSummaryTable = ({
    data,
    summarySelection: leftSelection,
    onSelectionChange,
    detailSelection: rightSelection,
    activeTable,
    dataLoaded
}) => {
    const [expandedMap, setExpandedMap] = useState({});
    const [selectedColumn, setSelectedColumn] = useState(null);
    const [firstColumnSelected, setFirstColumnSelected] = useState(false);
    const tableBodyRef = useRef(null);
    const rowRefs = useRef({});

    const hierarchicalData = useMemo(() => buildHierarchy(data), [data]);

    useEffect(() => {
        if (hierarchicalData.length > 0 && Object.keys(expandedMap).length === 0) {
            const initialExpanded = {};
            hierarchicalData.forEach(week => {
                initialExpanded[`week:${week.weekKey}`] = true;
            });
            setExpandedMap(initialExpanded);
        }
    }, [hierarchicalData]);

    useEffect(() => {
        if (rightSelection) {
            const weekKey = rightSelection.weekEnding
                ? new Date(rightSelection.weekEnding).toISOString()
                : null;
            const newExpanded = {};
            if (weekKey) {
                newExpanded[`week:${weekKey}`] = true;
                if (rightSelection.siteName) {
                    newExpanded[`site:${weekKey}:${rightSelection.siteName}`] = true;
                }
            }
            setExpandedMap(newExpanded);
        }
    }, [rightSelection]);

    useEffect(() => {
        if (leftSelection && leftSelection.type === 'weekRole' && leftSelection.weekEnding) {
            const weekKey = new Date(leftSelection.weekEnding).toISOString();
            const newExpandedMap = { [`week:${weekKey}`]: true };

            const week = hierarchicalData.find(w => w.weekKey === weekKey);
            if (week) {
                week.sites.forEach(site => {
                    const hasRole = site.roles.some(r =>
                        r.roleName?.toLowerCase() === leftSelection.roleName?.toLowerCase()
                    );
                    if (hasRole) {
                        newExpandedMap[`site:${weekKey}:${site.siteName}`] = true;
                    }
                });
            }

            setExpandedMap(prev => ({ ...prev, ...newExpandedMap }));

            setTimeout(() => {
                scrollToRow(`week:${weekKey}`);
            }, SCROLL_DELAY_MS);
        }
    }, [leftSelection, hierarchicalData]);

    const scrollToRow = (rowKey) => {
        const rowElement = rowRefs.current[rowKey];
        if (rowElement && tableBodyRef.current) {
            rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const toggleExpand = useCallback((key, e) => {
        e.stopPropagation();
        setExpandedMap(prev => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const handleColumnHeaderClick = useCallback((day) => {
        setFirstColumnSelected(false);
        if (selectedColumn === day) {
            setSelectedColumn(null);
            onSelectionChange(null);
        } else {
            setSelectedColumn(day);
            onSelectionChange({ type: 'columnDay', day });
        }
    }, [selectedColumn, onSelectionChange]);

    const handleCellClick = useCallback((selectionData) => {
        if (selectionData.type !== 'columnDay' && selectionData.type !== 'weekDay' &&
            selectionData.type !== 'siteDay' && selectionData.type !== 'roleDeptDay') {
            setSelectedColumn(null);
            setFirstColumnSelected(false);
        }

        if (
            leftSelection &&
            leftSelection.type === selectionData.type &&
            leftSelection.weekKey === selectionData.weekKey &&
            leftSelection.siteName === selectionData.siteName &&
            leftSelection.roleName === selectionData.roleName &&
            leftSelection.day === selectionData.day
        ) {
            onSelectionChange(null);
        } else {
            onSelectionChange(selectionData);
        }
    }, [leftSelection, onSelectionChange]);

    const isRowSelected = useCallback((type, weekKey = null, siteName = null, roleName = null) => {
        if (!leftSelection) return false;

        if (leftSelection.type === 'weekRole' && leftSelection.weekEnding) {
            const selWeekKey = new Date(leftSelection.weekEnding).toISOString();
            if (type === 'week') return weekKey === selWeekKey;
            if (type === 'roleDept') {
                return weekKey === selWeekKey &&
                    leftSelection.roleName?.toLowerCase() === roleName?.toLowerCase();
            }
            return false;
        }

        if (leftSelection.type === 'week' && type === 'week' &&
            leftSelection.weekKey === weekKey) return true;
        if (leftSelection.type === 'site' && type === 'site' &&
            leftSelection.weekKey === weekKey && leftSelection.siteName === siteName) return true;
        if (leftSelection.type === 'roleDept' && type === 'roleDept' &&
            leftSelection.weekKey === weekKey && leftSelection.siteName === siteName &&
            leftSelection.roleName === roleName) return true;
        return false;
    }, [leftSelection]);

    const handleFirstColumnClick = useCallback(() => {
        setSelectedColumn(null);
        if (firstColumnSelected) {
            setFirstColumnSelected(false);
            onSelectionChange(null);
        } else {
            setFirstColumnSelected(true);
            onSelectionChange({ type: 'all' });
        }
    }, [firstColumnSelected, onSelectionChange]);

    const handleDayCellClick = useCallback((e, selectionData) => {
        e.stopPropagation();
        handleCellClick(selectionData);
    }, [handleCellClick]);

    const displayData = useMemo(() => {
        if (leftSelection && leftSelection.type === 'weekRole' && leftSelection.weekEnding) {
            const selWeekKey = new Date(leftSelection.weekEnding).toISOString();
            return hierarchicalData
                .filter(w => w.weekKey === selWeekKey)
                .map(w => ({
                    ...w,
                    sites: w.sites.map(s => ({
                        ...s,
                        roles: s.roles.filter(r =>
                            r.roleName?.toLowerCase() === leftSelection.roleName?.toLowerCase()
                        )
                    })).filter(s => s.roles.length > 0)
                }));
        }

        if (activeTable !== 'right' || !rightSelection) return hierarchicalData;

        const rightWeekKey = rightSelection.weekEnding
            ? new Date(rightSelection.weekEnding).toISOString()
            : null;

        return hierarchicalData
            .filter(w => !rightWeekKey || w.weekKey === rightWeekKey)
            .map(w => ({
                ...w,
                sites: w.sites
                    .filter(s => s.siteName === rightSelection.siteName)
                    .map(s => ({
                        ...s,
                        roles: s.roles.filter(r => r.roleName === rightSelection.roleName)
                    }))
            }));
    }, [hierarchicalData, rightSelection, activeTable, leftSelection]);

    const filteredTotal = useMemo(() => {
        if (!rightSelection || !data) return null;
        const filteredRows = data.filter(row =>
            row.site_Name === rightSelection.siteName &&
            row.roleName === rightSelection.roleName &&
            (rightSelection.jobDay ? row.jobDay === rightSelection.jobDay : true)
        );
        return calculateDayTotals(filteredRows);
    }, [data, rightSelection]);

    if (!dataLoaded) {
        return (
            <div className="inline-loading">
                <Spinner animation="border" size="sm" />
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="costing-summary-empty">
                <div className="empty-text">No data available</div>
            </div>
        );
    }

    return (
        <div className="table-content" ref={tableBodyRef}>
            <table className="hierarchical-summary-table">
                    <thead>
                        <tr>
                            <th
                                className={`clickable-header ${firstColumnSelected ? 'selected-column' : ''}`}
                                onClick={handleFirstColumnClick}
                                style={{ cursor: 'pointer' }}
                                title="Click to show all data"
                            >
                                Week/Site/Role
                            </th>
                            {DAY_ORDER.map(day => (
                                <th
                                    key={day}
                                    className={`day-column clickable-header ${selectedColumn === day ? 'selected-column' : ''}`}
                                    onClick={() => handleColumnHeaderClick(day)}
                                    title={`Click to filter by ${day}`}
                                >
                                    {day.substring(0, 3)}
                                </th>
                            ))}
                            <th className="total-column">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                    {displayData.map(week => {
                        const weekKey = `week:${week.weekKey}`;
                        const isWeekExpanded = expandedMap[weekKey];
                        const weekTotals = calculateDayTotals(week.rows);

                        return (
                            <React.Fragment key={week.weekKey}>
                                <tr
                                    ref={el => rowRefs.current[weekKey] = el}
                                    className={`period-row ${isRowSelected('week', week.weekKey) ? 'selected-row' : ''}`}
                                    onClick={() => handleCellClick({
                                        type: 'week',
                                        weekKey: week.weekKey,
                                        weekEnding: week.weekEnding
                                    })}
                                >
                                    <td className={firstColumnSelected ? 'column-highlighted' : ''}>
                                        <span
                                            className="expand-icon"
                                            onClick={(e) => toggleExpand(weekKey, e)}
                                        >
                                            {isWeekExpanded ? <IoMdArrowDropdownCircle /> : <IoMdArrowDroprightCircle />}
                                        </span>
                                        W/E {formatWeekDate(week.weekEnding)}
                                    </td>
                                    {DAY_ORDER.map(day => (
                                        <td
                                            key={day}
                                            className={`clickable-cell ${selectedColumn === day ? 'column-highlighted' : ''}`}
                                            onClick={(e) => handleDayCellClick(e, {
                                                type: 'weekDay',
                                                weekKey: week.weekKey,
                                                weekEnding: week.weekEnding,
                                                day
                                            })}
                                        >
                                            <span title={formatCurrencyFull(weekTotals[day])}>
                                                {formatCurrencyCompact(weekTotals[day])}
                                            </span>
                                        </td>
                                    ))}
                                    <td className="total-cell" title={formatCurrencyFull(weekTotals.total)}>
                                        {formatCurrencyCompact(weekTotals.total)}
                                    </td>
                                </tr>

                                {isWeekExpanded && week.sites.map(site => {
                                    const siteKey = `site:${week.weekKey}:${site.siteName}`;
                                    const isSiteExpanded = expandedMap[siteKey];
                                    const siteTotals = calculateDayTotals(site.rows);

                                    return (
                                        <React.Fragment key={siteKey}>
                                            <tr
                                                ref={el => rowRefs.current[siteKey] = el}
                                                className={`week-row ${isRowSelected('site', week.weekKey, site.siteName) ? 'selected-row' : ''}`}
                                                onClick={() => handleCellClick({
                                                    type: 'site',
                                                    weekKey: week.weekKey,
                                                    weekEnding: week.weekEnding,
                                                    siteName: site.siteName
                                                })}
                                            >
                                                <td className={firstColumnSelected ? 'column-highlighted' : ''} style={{ paddingLeft: '1.5rem' }}>
                                                    <span
                                                        className="expand-icon"
                                                        onClick={(e) => toggleExpand(siteKey, e)}
                                                    >
                                                        {isSiteExpanded ? <IoMdArrowDropdownCircle /> : <IoMdArrowDroprightCircle />}
                                                    </span>
                                                    {site.siteName}
                                                </td>
                                                {DAY_ORDER.map(day => (
                                                    <td
                                                        key={day}
                                                        className={`clickable-cell ${selectedColumn === day ? 'column-highlighted' : ''}`}
                                                        onClick={(e) => handleDayCellClick(e, {
                                                            type: 'siteDay',
                                                            weekKey: week.weekKey,
                                                            weekEnding: week.weekEnding,
                                                            siteName: site.siteName,
                                                            day
                                                        })}
                                                    >
                                                        <span title={formatCurrencyFull(siteTotals[day])}>
                                                            {formatCurrencyCompact(siteTotals[day])}
                                                        </span>
                                                    </td>
                                                ))}
                                                <td className="total-cell" title={formatCurrencyFull(siteTotals.total)}>
                                                    {formatCurrencyCompact(siteTotals.total)}
                                                </td>
                                            </tr>

                                            {isSiteExpanded && site.roles.map(role => {
                                                const roleKey = `roleDept:${week.weekKey}:${site.siteName}:${role.roleName}`;
                                                const roleTotals = calculateDayTotals(role.rows);

                                                return (
                                                    <tr
                                                        key={roleKey}
                                                        ref={el => rowRefs.current[roleKey] = el}
                                                        className={`site-row ${isRowSelected('roleDept', week.weekKey, site.siteName, role.roleName) ? 'selected-row' : ''}`}
                                                        onClick={() => handleCellClick({
                                                            type: 'roleDept',
                                                            weekKey: week.weekKey,
                                                            weekEnding: week.weekEnding,
                                                            siteName: site.siteName,
                                                            roleName: role.roleName
                                                        })}
                                                    >
                                                        <td className={firstColumnSelected ? 'column-highlighted' : ''} style={{ paddingLeft: '3rem' }}>{role.roleName}</td>
                                                        {DAY_ORDER.map(day => (
                                                            <td
                                                                key={day}
                                                                className={`clickable-cell ${selectedColumn === day ? 'column-highlighted' : ''}`}
                                                                onClick={(e) => handleDayCellClick(e, {
                                                                    type: 'roleDeptDay',
                                                                    weekKey: week.weekKey,
                                                                    weekEnding: week.weekEnding,
                                                                    siteName: site.siteName,
                                                                    roleName: role.roleName,
                                                                    day
                                                                })}
                                                            >
                                                                {formatCurrencyCompact(roleTotals[day], { forceFullBelow: Infinity })}
                                                            </td>
                                                        ))}
                                                        <td className="total-cell">{formatCurrencyCompact(roleTotals.total, { forceFullBelow: Infinity })}</td>
                                                    </tr>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}

                        {rightSelection && filteredTotal && (
                            <tr className="period-total-row">
                                <td>Filtered Total</td>
                                {DAY_ORDER.map(day => (
                                    <td key={day} className={selectedColumn === day ? 'column-highlighted' : ''}>
                                        {formatCurrencyCompact(filteredTotal[day], { forceFullBelow: Infinity })}
                                    </td>
                                ))}
                                <td className="total-cell">{formatCurrencyCompact(filteredTotal.total, { forceFullBelow: Infinity })}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
        </div>
    );
};

HierarchicalSummaryTable.propTypes = {
    data: PropTypes.array,
    summarySelection: PropTypes.object,
    onSelectionChange: PropTypes.func.isRequired,
    detailSelection: PropTypes.object,
    activeTable: PropTypes.string,
    dataLoaded: PropTypes.bool
};

export default HierarchicalSummaryTable;
